import { useState, useRef, forwardRef, useMemo, useEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Trash2, ChefHat, Heart, CalendarPlus, ThumbsUp, ThumbsDown, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { useFavorites } from "@/hooks/useFavorites";
import { useRecipes } from "@/hooks/useRecipes";
import { useToast } from "@/hooks/use-toast";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  extractFirstJsonObjectFromStart,
  isIngredientObject,
  type ParsedIngredient,
  type IngredientWithSubstitute,
} from "@/utils/parseChatRecipes";
import { ingredientDisplayLabel, type IngredientItem } from "@/types/recipe";
import { useSubscription } from "@/hooks/useSubscription";
import { useAppStore } from "@/store/useAppStore";
import { AddToPlanSheet } from "@/components/plan/AddToPlanSheet";
import { HelpSectionCard } from "@/components/help-ui";
import { HelpDoctorReminderLine } from "@/components/help/HelpDoctorReminderLine";
import { safeError } from "@/utils/safeLogger";
import { getBenefitLabel } from "@/utils/ageCategory";
import { buildRecipeShareTextForShare, SHARE_APP_URL } from "@/utils/shareRecipeText";
import { copyShareTextToClipboard } from "@/utils/shareActions";
import ChatRecipeCard from "@/components/chat/ChatRecipeCard";
import { SystemHintCard } from "@/components/chat/SystemHintCard";
import { ConfirmActionModal } from "@/components/ui/confirm-action-modal";
import type { SystemHintRoute } from "@/utils/chatRouteFallback";
import { ShareIosIcon } from "@/components/icons/ShareIosIcon";
import {
  trackUsageEvent,
  generateShareRef,
  getShareChannelFromContext,
  getShortShareUrl,
  saveShareRef,
  getShareRecipeUrl,
  trackShareLinkCreated,
} from "@/utils/usageEvents";
import { shouldShowHelpDoctorReminder, stripHelpDoctorSection } from "@/utils/stripHelpDoctorSection";

/** Убирает ведущий JSON (сырой или в блоке ```json) из ответа ИИ — в чате только читаемый текст. */
function getTextForDisplay(content: string): string {
  let t = content.trim();
  // Удаляем ведущий code block ```json ... ``` или ``` ... ```
  t = t.replace(/^```(?:json)?\s*\n[\s\S]*?```\s*/i, "").trim();
  // Удаляем сырой JSON в начале
  if (t.startsWith("{")) {
    const jsonStr = extractFirstJsonObjectFromStart(t);
    if (jsonStr) {
      const idx = t.indexOf(jsonStr);
      t = t.slice(idx + jsonStr.length).trim();
    }
  }
  return t || content;
}

interface ChatMessageProps {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  rawContent?: string;
  onDelete: (id: string) => void;
  /** true = ответ должен быть рецептом (JSON); при null от парсера показываем ошибку, не текст */
  expectRecipe?: boolean;
  /** Контекст члена семьи для сохранения в избранное */
  memberId?: string;
  memberName?: string;
  /** Возраст выбранного члена (мес.) для подписи «Польза»: ребёнок / взрослый / нейтрально */
  ageMonths?: number | null;
  /** Профиль в карусели: id члена или "family" — тон текста блока пользы */
  selectedProfileId?: string | null;
  /** Уже распарсенный рецепт (из parseRecipesFromChat), чтобы не показывать «Данные повреждены» при расхождении парсеров */
  preParsedRecipe?: Recipe | null;
  /** ID рецепта в БД (от ChatPage после saveRecipesFromChat), для favorites_v2.recipe_id */
  recipeId?: string | null;
  /** true = ответ ещё стримится; не показываем ошибку парсинга до завершения */
  isStreaming?: boolean;
  /** В режиме help: всегда показывать сообщение как текст, без парсинга рецептов и без RecipeCard */
  forcePlainText?: boolean;
  /** Режим консультации (Help Chat): карточка рекомендации, без action icons */
  isConsultationMode?: boolean;
  /** Ответ-отказ по аллергии/dislike: скрываем кнопки лайк, шэринг, в план */
  isBlockedRefusal?: boolean;
  /** Системная подсказка (редирект в Помощник / нерелевантность) — рендерить SystemHintCard, без кнопок рецепта */
  systemHintType?: SystemHintRoute;
  topicKey?: string;
  topicTitle?: string;
  topicShortTitle?: string;
  onOpenAssistant?: (topicKey?: string) => void;
  /** Доп. кнопки в SystemHintCard (curated 0–11 мес: план + Помощь маме). */
  systemHintExtraActions?: ReactNode;
}

type MealType = 'breakfast' | 'lunch' | 'snack' | 'dinner';

interface Recipe {
  title: string;
  description?: string;
  ingredients?: ParsedIngredient[];
  steps?: string[];
  cookingTime?: number;
  ageRange?: string;
  /** Premium: совет от шефа */
  chefAdvice?: string;
  /** Free: короткий мини-совет (поле advice в JSON) */
  advice?: string;
  familyServing?: string;
  mealType?: MealType;
  calories?: number | null;
  proteins?: number | null;
  fats?: number | null;
  carbs?: number | null;
  nutrition_goals?: string[] | null;
}

/** Чат = child_only: НЕ показываем familyServing (Адаптация для ребёнка/взрослых). */
const MEAL_LABELS: Record<MealType, string> = {
  breakfast: 'Завтрак',
  lunch: 'Обед',
  dinner: 'Ужин',
  snack: 'Перекус',
};

/**
 * Форматирует рецепт в красивый вид (для шаринга и т.д.)
 */
function formatRecipe(recipe: Recipe): string {
  let formatted = `🍽️ **${recipe.title}**\n\n`;

  if (recipe.description) {
    formatted += `${recipe.description}\n\n`;
  }

  if (recipe.cookingTime) {
    formatted += `⏱️ Время приготовления: ${recipe.cookingTime} мин\n\n`;
  }

  if (recipe.ingredients && recipe.ingredients.length > 0) {
    formatted += `**Ингредиенты:**\n`;
    recipe.ingredients.forEach((ing, index) => {
      formatted += `${index + 1}. ${typeof ing === "string" ? ing : ingredientDisplayLabel(ing as unknown as IngredientItem)}\n`;
    });
    formatted += `\n`;
  }

  if (recipe.steps && recipe.steps.length > 0) {
    formatted += `**Приготовление:**\n`;
    recipe.steps.forEach((step, index) => {
      formatted += `${index + 1}. ${step}\n`;
    });
  }

  return formatted;
}

const RECIPE_UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
function isValidRecipeId(v: string): boolean {
  return typeof v === "string" && v.length > 0 && RECIPE_UUID_REGEX.test(v);
}

export const ChatMessage = forwardRef<HTMLDivElement, ChatMessageProps>(
  ({ id, role, content, timestamp, rawContent, expectRecipe, preParsedRecipe, recipeId: recipeIdProp, isStreaming, onDelete, memberId, memberName, ageMonths, selectedProfileId, forcePlainText = false, isConsultationMode = false, isBlockedRefusal = false, systemHintType, topicKey, topicTitle, topicShortTitle, onOpenAssistant, systemHintExtraActions }, ref) => {
    const [showDelete, setShowDelete] = useState(false);
    const [localRecipeId, setLocalRecipeId] = useState<string | null>(null);
    const [addToPlanOpen, setAddToPlanOpen] = useState(false);
    const [userVote, setUserVote] = useState<"like" | "dislike" | null>(null);
    const { user } = useAuth();
    const { isPremium, isTrial, favoritesLimit, hasAccess } = useSubscription();
    const showChefTip = isPremium || isTrial;
    const { favorites, isFavorite: isFavoriteFn, toggleFavorite, isToggling } = useFavorites("all");
    const { createRecipe } = useRecipes();
    const chatMemberId = memberId ?? null;
    const setShowPaywall = useAppStore((s) => s.setShowPaywall);
    const setPaywallCustomMessage = useAppStore((s) => s.setPaywallCustomMessage);
    const setPaywallReason = useAppStore((s) => s.setPaywallReason);
    const { toast } = useToast();
    const location = useLocation();
    const planSlotState =
      (location.state as {
        fromPlanSlot?: boolean;
        plannedDate?: string;
        mealType?: string;
        memberId?: string | null;
      } | null) ?? null;

    const [ingredientOverrides, setIngredientOverrides] = useState<Record<number, string>>({});

    const effectiveRecipe = forcePlainText ? null : (preParsedRecipe ?? null);
    /** Редирект в Помощник или нерелевантный ответ — показывать content как есть, не как «ошибку парсинга» */
    const isRedirectOrIrrelevantContent =
      role === "assistant" &&
      (content.includes("Этот чат помогает подбирать рецепты") ||
        content.includes("В этом чате мы помогаем подбирать блюда") ||
        content.includes("Мы помогаем подбирать блюда и идеи для готовки") ||
        (content.includes("Этот вопрос лучше задать во вкладке «Помощник»") ||
          content.includes("Этот вопрос лучше задать во вкладке «Помощь маме»") ||
          content.includes("Похоже, этот вопрос лучше разобрать во вкладке «Помощь маме»")));
    const isRecipeParseFailure =
      !forcePlainText &&
      role === "assistant" &&
      !isRedirectOrIrrelevantContent &&
      (expectRecipe === true || (rawContent != null && rawContent.trim().length > 0)) &&
      effectiveRecipe === null;
    /** Ошибку показываем только после завершения стрима; во время генерации — loader, без мигания */
    const showParseError = !isStreaming && isRecipeParseFailure;
    const hasSubstitutes = isPremium && effectiveRecipe?.ingredients?.some((ing) => isIngredientObject(ing) && (ing as { substitute?: string }).substitute);
    // Для отображения: в forcePlainText (help) показываем как есть; иначе убираем ведущий JSON
    const displayContent =
      role === "assistant"
        ? (forcePlainText ? content : getTextForDisplay(content))
        : content;

    const recipeId = recipeIdProp ?? localRecipeId;
    const isFavorite = !!(recipeId && isValidRecipeId(recipeId) && isFavoriteFn(recipeId, chatMemberId));
    const canRecipeFeedback = !!(recipeId && isValidRecipeId(recipeId) && user);

    useEffect(() => {
      const rid = recipeId;
      if (!rid || !isValidRecipeId(rid) || !user) {
        setUserVote(null);
        return;
      }
      let cancelled = false;
      void (async () => {
        try {
          const { data } = await (supabase.rpc as (n: string, a: Record<string, string>) => Promise<{ data: unknown }>)(
            "get_recipe_my_vote",
            { p_recipe_id: rid }
          );
          if (cancelled) return;
          if (data === "like") setUserVote("like");
          else if (data === "dislike") setUserVote("dislike");
          else setUserVote(null);
        } catch {
          if (!cancelled) setUserVote(null);
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [recipeId, user]);

    const handleRecipeFeedback = async (action: "like" | "dislike") => {
      const rid = recipeId;
      if (!rid || !isValidRecipeId(rid) || !user) return;
      if (userVote === action) return;
      try {
        await (supabase.rpc as (n: string, a: Record<string, string>) => Promise<unknown>)("record_recipe_feedback", {
          p_recipe_id: rid,
          p_action: action,
        });
        setUserVote(action);
        toast({ title: action === "like" ? "Спасибо за отзыв" : "Учли" });
      } catch {
        toast({ variant: "destructive", title: "Не удалось отправить" });
      }
    };

    const handleToggleFavorite = async () => {
      if (!effectiveRecipe) return;
      if (isFavorite) {
        try {
          if (recipeId && isValidRecipeId(recipeId)) {
            await toggleFavorite({ recipeId, memberId: chatMemberId, isFavorite: false });
          }
          toast({ title: "Удалено из избранного" });
        } catch (e: unknown) {
          safeError("ChatMessage removeFavorite:", (e as Error).message);
          toast({ title: "Не удалось удалить из избранного", variant: "destructive", description: (e as Error).message });
        }
        return;
      }
      // Free: лимит 10 избранных
      if (!showChefTip && favorites.length >= (favoritesLimit ?? 10)) {
        setPaywallReason("favorites_limit");
        setPaywallCustomMessage(null);
        setShowPaywall(true);
        return;
      }
      let idToFavorite = recipeId && isValidRecipeId(recipeId) ? recipeId : null;
      if (!idToFavorite) {
        try {
          const validChildId =
            memberId && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(memberId)
              ? memberId
              : null;
          const cookingMinutes =
            effectiveRecipe.cookingTime != null
              ? Math.floor(typeof effectiveRecipe.cookingTime === "number" ? effectiveRecipe.cookingTime : parseInt(String(effectiveRecipe.cookingTime), 10))
              : null;
          const newRecipe = await createRecipe({
            source: "chat_ai",
            canonicalBenefitPersist: {
              chatMessageId: id,
              nutritionGoals: (effectiveRecipe as { nutrition_goals?: string[] | null }).nutrition_goals ?? null,
            },
            recipe: {
              title: effectiveRecipe.title,
              description: "",
              nutrition_goals: (effectiveRecipe as { nutrition_goals?: string[] | null }).nutrition_goals ?? [],
              cooking_time_minutes: Number.isFinite(cookingMinutes) ? cookingMinutes : null,
              calories: (effectiveRecipe as { calories?: number | null }).calories ?? null,
              proteins: (effectiveRecipe as { proteins?: number | null }).proteins ?? null,
              fats: (effectiveRecipe as { fats?: number | null }).fats ?? null,
              carbs: (effectiveRecipe as { carbs?: number | null }).carbs ?? null,
              member_id: validChildId,
              child_id: validChildId,
              tags: (effectiveRecipe as { mealType?: string }).mealType
                ? ["chat", `chat_${(effectiveRecipe as { mealType: string }).mealType}`]
                : ["chat"],
              ...((effectiveRecipe as { mealType?: string }).mealType && {
                meal_type: (effectiveRecipe as { mealType: string }).mealType,
              }),
              chef_advice: effectiveRecipe.chefAdvice?.trim() || null,
              advice: effectiveRecipe.advice?.trim() || null,
            },
            ingredients: (effectiveRecipe.ingredients || []).map((ing, index) => {
              const o = typeof ing === "object" && ing && "name" in ing ? (ing as { name: string; display_text?: string | null; canonical_amount?: number | null; canonical_unit?: string | null; substitute?: string }) : null;
              const nameStr = o?.name ?? (typeof ing === "string" ? ing : String(ing));
              const displayText = o?.display_text;
              const canonical = o?.canonical_amount != null && (o?.canonical_unit === "g" || o?.canonical_unit === "ml") ? { amount: o.canonical_amount, unit: o.canonical_unit as "g" | "ml" } : null;
              return {
                name: nameStr,
                display_text: displayText ?? null,
                canonical_amount: canonical?.amount ?? null,
                canonical_unit: canonical?.unit ?? null,
                amount: null,
                unit: null,
                category: "other" as const,
                order_index: index,
                ...(o?.substitute != null && o.substitute !== "" && { substitute: String(o.substitute) }),
              };
            }),
            steps: (effectiveRecipe.steps || []).map((step, index) => ({
              instruction: step,
              step_number: index + 1,
              duration_minutes: null,
              image_url: null,
            })),
          });
          idToFavorite = newRecipe.id;
          setLocalRecipeId(newRecipe.id);
        } catch (e: unknown) {
          safeError("ChatMessage createRecipe:", (e as Error).message);
          toast({ title: "Не удалось добавить в избранное", variant: "destructive", description: (e as Error).message });
          return;
        }
      }
      try {
        const preview = {
          title: effectiveRecipe.title,
          description: effectiveRecipe.description ?? null,
          cookTimeMinutes: effectiveRecipe.cookingTime ?? null,
          ingredientNames: (effectiveRecipe.ingredients || []).map((ing) =>
            typeof ing === "string" ? ing : (ing as { name?: string }).name ?? ""
          ),
          chefAdvice: effectiveRecipe.chefAdvice ?? null,
          advice: effectiveRecipe.advice ?? null,
        };
        await toggleFavorite({
          recipeId: idToFavorite!,
          memberId: chatMemberId,
          isFavorite: true,
          recipeData: {
            title: effectiveRecipe.title,
            description: effectiveRecipe.description ?? null,
            cookTimeMinutes: effectiveRecipe.cookingTime ?? null,
            ingredientNames: (effectiveRecipe.ingredients || []).map((ing) =>
              typeof ing === "string" ? ing : (ing as { name?: string }).name ?? ""
            ),
            chefAdvice: effectiveRecipe.chefAdvice ?? null,
            advice: effectiveRecipe.advice ?? null,
          },
        });
        toast({ title: "Добавлено в избранное" });
      } catch (e: unknown) {
        safeError("ChatMessage toggleFavorite:", (e as Error).message);
        toast({ title: "Не удалось добавить в избранное", variant: "destructive", description: (e as Error).message });
      }
    };

    const shareText = useMemo(() => {
      if (effectiveRecipe?.title) {
        return null;
      }
      return typeof content === "string" ? content + "\n\n— Рецепт из приложения Mom Recipes\n" + SHARE_APP_URL : "";
    }, [effectiveRecipe, content]);

    const handleShare = async () => {
      const rid = recipeId ?? undefined;
      const shareRef = generateShareRef();
      const usedNativeShare = typeof navigator !== "undefined" && !!navigator.share;
      const channel = getShareChannelFromContext(usedNativeShare, false);
      let shareUrl = SHARE_APP_URL;
      if (rid) {
        const saved = await saveShareRef(rid, shareRef);
        if (saved) {
          trackShareLinkCreated({
            share_type: "recipe",
            share_ref: shareRef,
            surface: "chat",
            recipe_id: rid,
            has_native_share: usedNativeShare,
          });
        }
        shareUrl = saved
          ? getShortShareUrl(shareRef, SHARE_APP_URL)
          : getShareRecipeUrl(rid, channel, shareRef, SHARE_APP_URL);
      }
      const title = effectiveRecipe?.title ?? "Рецепт";
      const textToShare = effectiveRecipe?.title
        ? buildRecipeShareTextForShare(
            {
              title: effectiveRecipe.title,
              description: effectiveRecipe.description,
              cookingTime: effectiveRecipe.cookingTime,
              mealType: effectiveRecipe.mealType,
              mealTypeLabel: effectiveRecipe.mealType
                ? MEAL_LABELS[effectiveRecipe.mealType]
                : undefined,
              ingredients: effectiveRecipe.ingredients as IngredientItem[] | undefined,
              steps: effectiveRecipe.steps,
              chefAdvice: effectiveRecipe.chefAdvice ?? effectiveRecipe.advice,
            },
            rid ?? "",
            shareUrl
          )
        : shareText || "";
      if (!textToShare) return;
      trackUsageEvent("share_click", {
        properties: {
          ...(rid ? { recipe_id: rid } : {}),
          share_ref: shareRef,
          channel,
          source_screen: "chat",
        },
      });
      let shareCompleted = false;
      try {
        if (typeof navigator !== "undefined" && navigator.share) {
          await navigator.share({
            title,
            text: textToShare,
          });
          toast({ title: "Поделиться", description: "Рецепт отправлен" });
          shareCompleted = true;
        } else {
          const canCopy = typeof navigator !== "undefined" && typeof navigator.clipboard?.writeText === "function";
          if (!canCopy) {
            toast({
              variant: "destructive",
              title: "Копирование недоступно",
              description: "В этом браузере нельзя скопировать рецепт. Скопируйте вручную.",
            });
            return;
          }
          await navigator.clipboard.writeText(textToShare);
          toast({ title: "Рецепт скопирован для отправки" });
          shareCompleted = true;
        }
      } catch (e: any) {
        if (e?.name !== "AbortError") {
          toast({ variant: "destructive", title: "Ошибка", description: e.message || "Не удалось поделиться" });
        }
      }
      if (shareCompleted && rid && isValidRecipeId(rid) && user) {
        try {
          await (supabase.rpc as (n: string, a: Record<string, string>) => Promise<unknown>)(
            "record_recipe_feedback",
            { p_recipe_id: rid, p_action: "shared" }
          );
        } catch {
          /* scoring не критичен для UX */
        }
      }
    };

    const handleCopyShare = async () => {
      const rid = recipeId ?? undefined;
      const shareRef = generateShareRef();
      const channel = getShareChannelFromContext(false, true);
      let shareUrl = SHARE_APP_URL;
      if (rid) {
        const saved = await saveShareRef(rid, shareRef);
        if (saved) {
          trackShareLinkCreated({
            share_type: "recipe",
            share_ref: shareRef,
            surface: "chat",
            recipe_id: rid,
            has_native_share: false,
          });
        }
        shareUrl = saved
          ? getShortShareUrl(shareRef, SHARE_APP_URL)
          : getShareRecipeUrl(rid, channel, shareRef, SHARE_APP_URL);
      }
      const textToShare = effectiveRecipe?.title
        ? buildRecipeShareTextForShare(
            {
              title: effectiveRecipe.title,
              description: effectiveRecipe.description,
              cookingTime: effectiveRecipe.cookingTime,
              mealType: effectiveRecipe.mealType,
              mealTypeLabel: effectiveRecipe.mealType
                ? MEAL_LABELS[effectiveRecipe.mealType]
                : undefined,
              ingredients: effectiveRecipe.ingredients as IngredientItem[] | undefined,
              steps: effectiveRecipe.steps,
              chefAdvice: effectiveRecipe.chefAdvice ?? effectiveRecipe.advice,
            },
            rid ?? "",
            shareUrl
          )
        : shareText || "";
      if (!textToShare) return;
      trackUsageEvent("share_click", {
        properties: {
          ...(rid ? { recipe_id: rid } : {}),
          share_ref: shareRef,
          channel: "copy_link",
          source_screen: "chat",
        },
      });
      if (await copyShareTextToClipboard(textToShare)) {
        toast({ title: "Скопировано", description: "Вставьте текст в WhatsApp или Telegram" });
        if (rid && isValidRecipeId(rid) && user) {
          try {
            await (supabase.rpc as (n: string, a: Record<string, string>) => Promise<unknown>)(
              "record_recipe_feedback",
              { p_recipe_id: rid, p_action: "shared" }
            );
          } catch {
            /* ignore */
          }
        }
      } else {
        toast({ variant: "destructive", title: "Не удалось скопировать текст" });
      }
    };

    const handleDelete = () => {
      onDelete(id);
      setShowDelete(false);
    };

    return (
      <div
        ref={ref}
        data-message-id={id}
        className={`relative flex ${role === "user" ? "justify-end" : "justify-start"} ${role === "assistant" && effectiveRecipe ? "scroll-mt-[60px]" : ""}`}
      >
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: "easeOut" }}
          exit={{ opacity: 0 }}
          className={`relative ${role === "user" ? "max-w-[80%]" : "max-w-[96%]"}`}
        >
          {(() => {
            const isConsultationBubble = role === "assistant" && forcePlainText;
            /** Карточка как у системных подсказок (Info, единый фон) — редиректы, 0–11 мес, отказ аллергия/dislike */
            const isSystemHintCardLayout =
              role === "assistant" && (!!systemHintType || isBlockedRefusal);
            const Wrapper = isConsultationBubble ? HelpSectionCard : "div";
            const wrapperClassName = isSystemHintCardLayout
              ? "relative max-w-full"
              : isConsultationBubble
                ? "rounded-2xl rounded-bl-sm border border-border bg-card shadow-soft p-4"
                : role === "user"
                  ? "relative p-3 text-xs bg-primary text-primary-foreground rounded-2xl rounded-br-sm break-words leading-snug border border-primary/25 shadow-soft"
                  : role === "assistant" && effectiveRecipe
                    ? "relative p-0 overflow-visible"
                    : role === "assistant" && isStreaming
                      ? "relative py-2 pr-3 pl-1 min-w-0 bg-transparent border-0 shadow-none"
                      : "relative p-3 rounded-2xl bg-card border border-border shadow-soft";
            return (
              <Wrapper className={wrapperClassName}>
            {role === "assistant" && (systemHintType || isBlockedRefusal) ? (
              <SystemHintCard
                text={content}
                topicKey={systemHintType ? topicKey : undefined}
                topicShortTitle={systemHintType ? topicShortTitle : undefined}
                onOpenAssistant={systemHintType ? onOpenAssistant : undefined}
                extraActions={systemHintType ? systemHintExtraActions : undefined}
                actionSlot={
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowDelete(true);
                    }}
                    className="h-9 w-9 rounded-full flex items-center justify-center text-muted-foreground bg-muted/50 border border-border hover:bg-muted hover:text-foreground transition-all active:scale-95"
                    title="Удалить"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                }
                timestamp={timestamp}
              />
            ) : role === "assistant" && showParseError ? (
              <div className="text-xs text-muted-foreground space-y-1">
                <p>Не удалось распознать рецепт. Попробуйте уточнить запрос.</p>
                {rawContent?.trim() && !rawContent.trim().startsWith("{") && rawContent.length < 500 ? (
                  <p className="text-[10px] opacity-80 pt-0.5 border-t border-border/50 mt-1">{rawContent.trim().slice(0, 300)}</p>
                ) : null}
              </div>
            ) : role === "assistant" && effectiveRecipe ? (
              <>
                <ChatRecipeCard
                  recipe={effectiveRecipe}
                  ageMonths={ageMonths}
                  selectedProfileId={selectedProfileId ?? null}
                  chatMessageId={id}
                  savedRecipeId={recipeId}
                  showChefTip={showChefTip}
                  ingredientOverrides={ingredientOverrides}
                  onSubstituteClick={() => {
                    toast({
                      title: "Скоро будет доступно",
                      description: "Замена ингредиентов в разработке. Мы дорабатываем эту функцию для вас.",
                    });
                  }}
                />
              </>
            ) : role === "assistant" ? (
              <div className={`chat-message-content text-xs select-none prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-p:text-foreground prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-li:text-foreground prose-strong:text-foreground [&>*]:text-foreground ${forcePlainText ? "consultationCard-inner" : ""}`}>
                {forcePlainText ? (() => {
                  const main = stripHelpDoctorSection(displayContent);
                  const showDoctorReminder = shouldShowHelpDoctorReminder(id);
                  return (
                    <>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{main}</ReactMarkdown>
                      {showDoctorReminder ? <HelpDoctorReminderLine /> : null}
                      <p className="consultationDisclaimer">Это справочная информация.</p>
                    </>
                  );
                })() : (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {displayContent}
                </ReactMarkdown>
                )}
              </div>
            ) : (
              <p className="text-typo-muted whitespace-pre-wrap select-none leading-snug break-words">{displayContent}</p>
            )}
            {!forcePlainText && !systemHintType && !isBlockedRefusal && (role !== "assistant" || !isStreaming) && (
            <p className={`text-xs mt-1.5 ${role === "user" ? "text-primary-foreground/90" : "text-muted-foreground"}`}>
              {timestamp.toLocaleTimeString("ru-RU", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
            )}
            {role === "assistant" && !isStreaming && !isConsultationMode && !systemHintType && !isBlockedRefusal && (
              <div
                className="flex flex-row items-center justify-between gap-2 mt-3 pt-3 border-t border-border/50 shrink-0"
                style={{ touchAction: "manipulation" }}
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onPointerDownCapture={(e) => e.stopPropagation()}
              >
                {!isBlockedRefusal ? (
                  <div className="flex flex-row gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleToggleFavorite();
                      }}
                      disabled={isToggling}
                      className={`h-9 w-9 rounded-full shrink-0 flex items-center justify-center transition-all active:scale-95 ${isFavorite
                        ? "text-primary bg-primary/10 border border-primary/20 fill-primary"
                        : "text-muted-foreground bg-muted/50 border border-border hover:bg-muted hover:text-foreground"
                        }`}
                      title="В избранное"
                    >
                      <Heart className={`h-4 w-4 ${isFavorite ? "fill-current" : ""}`} />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleShare();
                      }}
                      disabled={!(effectiveRecipe?.title || shareText)}
                      className="h-9 w-9 rounded-full shrink-0 flex items-center justify-center text-muted-foreground bg-muted/50 border border-border hover:bg-muted hover:text-foreground disabled:opacity-50 transition-all active:scale-95"
                      title="Поделиться"
                    >
                      <ShareIosIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        void handleCopyShare();
                      }}
                      disabled={!(effectiveRecipe?.title || shareText)}
                      className="h-9 w-9 rounded-full shrink-0 flex items-center justify-center text-muted-foreground bg-muted/50 border border-border hover:bg-muted hover:text-foreground disabled:opacity-50 transition-all active:scale-95"
                      title="Скопировать текст"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    {canRecipeFeedback ? (
                      <>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            void handleRecipeFeedback("like");
                          }}
                          className={cn(
                            "h-9 w-9 rounded-full shrink-0 flex items-center justify-center transition-all border active:scale-95",
                            userVote === "like"
                              ? "text-primary bg-primary/10 border-primary/20 fill-primary"
                              : "text-muted-foreground bg-muted/50 border-border hover:bg-muted hover:text-foreground"
                          )}
                          aria-label={userVote === "like" ? "Нравится (выбрано)" : "Нравится"}
                          title="Нравится"
                        >
                          <ThumbsUp className={cn("h-4 w-4", userVote === "like" && "fill-current")} />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            void handleRecipeFeedback("dislike");
                          }}
                          className={cn(
                            "h-9 w-9 rounded-full shrink-0 flex items-center justify-center transition-all border active:scale-95",
                            userVote === "dislike"
                              ? "text-destructive bg-destructive/10 border-destructive/20 fill-destructive"
                              : "text-muted-foreground bg-muted/50 border-border hover:bg-muted hover:text-foreground"
                          )}
                          aria-label={userVote === "dislike" ? "Не нравится (выбрано)" : "Не нравится"}
                          title="Не нравится"
                        >
                          <ThumbsDown className={cn("h-4 w-4", userVote === "dislike" && "fill-current")} />
                        </button>
                      </>
                    ) : null}
                    {hasAccess ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (recipeId && isValidRecipeId(recipeId)) setAddToPlanOpen(true);
                        }}
                        disabled={!recipeId || !isValidRecipeId(recipeId)}
                        className="h-9 w-9 rounded-full shrink-0 flex items-center justify-center text-muted-foreground bg-muted/50 border border-border hover:bg-muted hover:text-foreground disabled:opacity-50 transition-all active:scale-95"
                        title="В план"
                      >
                        <CalendarPlus className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setPaywallReason("add_to_plan");
                          setPaywallCustomMessage(null);
                          setShowPaywall(true);
                        }}
                        className="h-9 w-9 rounded-full shrink-0 flex items-center justify-center text-muted-foreground bg-muted/50 border border-border hover:bg-muted hover:text-foreground transition-all active:scale-95"
                        title="В план (полная версия)"
                      >
                        <CalendarPlus className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ) : (
                  <div />
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowDelete(true);
                  }}
                  className="h-9 w-9 rounded-full shrink-0 flex items-center justify-center text-muted-foreground bg-muted/50 border border-border hover:bg-muted hover:text-foreground transition-all active:scale-95"
                  title="Удалить"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            )}
              </Wrapper>
            );
          })()}
        </motion.div>

        <ConfirmActionModal
          open={showDelete}
          onOpenChange={setShowDelete}
          title="Удалить сообщение?"
          description="Это действие нельзя отменить"
          confirmText="Удалить"
          cancelText="Отмена"
          onConfirm={handleDelete}
        />

        {addToPlanOpen && recipeId && isValidRecipeId(recipeId) && effectiveRecipe && (
          <AddToPlanSheet
            open={addToPlanOpen}
            onOpenChange={setAddToPlanOpen}
            recipeId={recipeId}
            recipeTitle={effectiveRecipe.title ?? "Рецепт"}
            mealType={
              planSlotState?.fromPlanSlot && planSlotState?.mealType
                ? planSlotState.mealType
                : (effectiveRecipe as { mealType?: string }).mealType ?? null
            }
            defaultMemberId={
              planSlotState?.fromPlanSlot && planSlotState != null && Object.prototype.hasOwnProperty.call(planSlotState, "memberId")
                ? planSlotState.memberId ?? null
                : chatMemberId
            }
            defaultDayKey={
              planSlotState?.fromPlanSlot && planSlotState?.plannedDate
                ? planSlotState.plannedDate
                : undefined
            }
            targetSlot={
              planSlotState?.fromPlanSlot && planSlotState?.plannedDate && planSlotState?.mealType
                ? { dayKey: planSlotState.plannedDate, mealType: planSlotState.mealType }
                : null
            }
            onSuccess={() => toast({ title: "Добавлено в план" })}
          />
        )}
      </div>
    );
  }
);

ChatMessage.displayName = "ChatMessage";
